/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactNode, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
interface ITariffProps {
  tariffResults: any;
  productValue: number;
  materialPct?: number;
  dutyCost?: number;
  totalCost?: number;
  baseCost?: number;
  displayAdditionalRate?: string;
  tariffCandidates?: any[];
  enabledExclusions?: Record<string, boolean>;
  toggleExclusion?: any;
  ftaOptions?: FtaOption[];
  enabledFtas?: Record<string, boolean>;
  toggleFta?: any;
  explanationText: string;
  aiLoading: boolean;
  adcvdResults?: string;
  aiExplain?: any;
  mpf?: number;
  hmf?: number;
}
type FtaOption = { code: string; name: string };

const ExpComp: React.FC<ITariffProps> = ({
  tariffResults,
  explanationText,
  aiLoading,
  aiExplain,
}) => {
  const safeTariffResults =
    tariffResults && typeof tariffResults === 'object' ? tariffResults : {};
  // --- Normalize "live" vs "snapshot" shapes --------------------------------
  // Read-only viewers (ReadOnlyTariffMirror, TariffResultsComp) never pass
  // aiExplain — they just display a saved snapshot's own explanation.text.
  // MergeTariff.tsx's live workflow DOES pass aiExplain, but when it's
  // hydrated from a resumed ticket (see initialResults in MergeTariff.tsx),
  // tariffResults can ALSO happen to carry the same inputs/base/results
  // snapshot shape — without the `!aiExplain` check, that made this component
  // treat a fully-interactive session as read-only, so clicking "Explain
  // Results" never showed the loading state or the live result (it only ever
  // rendered the snapshot's static explanation.text, which stays empty until
  // a save round-trips it). aiExplain being provided is the real signal for
  // "this is interactive," regardless of what shape the data happens to be.
  const isSnapshot = Boolean(
    !aiExplain &&
      safeTariffResults.inputs &&
      safeTariffResults.base &&
      safeTariffResults.results,
  );

  const savedExplanation: string = isSnapshot
    ? String(safeTariffResults.explanation?.text || '')
    : '';

  const explanationToShow = isSnapshot
    ? savedExplanation
    : explanationText || '';
  const aiBusy = isSnapshot ? false : aiLoading;

  const [dots, setDots] = React.useState('');

  useEffect(() => {
    if (!aiLoading) {
      setDots('');
      return undefined;
    }
    const id = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '' : `${d}.`));
    }, 350);
    return () => clearInterval(id);
  }, [aiLoading]);

  // useEffect(() => {
  //   aiExplain();
  // }, []);

  const shouldShowAiSection =
    explanationToShow.length > 0 ||
    (!isSnapshot && (Boolean(aiExplain) || aiBusy));

  return (
    <>
      <div
        className='api-container-left'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div className='screening-ai-containers'>
          <div style={{ width: '100%' }}>
            <div className='api-container-left'>
              <div
                className='screening-ai-label-text'
                style={{
                  marginBottom: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <AutoAwesomeIcon
                  style={{ marginRight: '10px', color: '#4895ef' }}
                />
                BITE&apos;s Explanation:
              </div>
              <p
                className='screening-ai-sum-result-text'
                style={{ margin: '2px 0 8px', opacity: 0.85 }}
              >
                Press &apos;Explain Results&apos; below to receive a summary
                explanation for each duty and/or tariff line item
              </p>

              {/* Controls */}
              <div className='screening-input-submit-group'>
                <button
                  className='btn-new-automate'
                  onClick={aiExplain}
                  disabled={aiLoading}
                >
                  <div>
                    {explanationToShow.length
                      ? 'Re-run Explanation'
                      : 'Explain Results'}
                  </div>
                </button>
              </div>
              <div className='screening-ai-sum-groups'>
                <div
                  className='screening-ai-sum-header-texts'
                  data-usetiful={
                    !aiBusy && explanationToShow.length > 0
                      ? 'tariff-results-done'
                      : ''
                  }
                >
                  <ReactMarkdown
                    components={{
                      h1: ({ children }: { children: ReactNode }) => (
                        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                          {children}
                        </h1>
                      ),
                      h2: ({ children }: { children: ReactNode }) => (
                        <h2 style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                          {children}
                        </h2>
                      ),
                      h3: ({ children }: { children: ReactNode }) => (
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                          {children}
                        </h3>
                      ),
                      p: ({ children }: { children: ReactNode }) => (
                        <p style={{ marginBottom: '0.75rem' }}>{children}</p>
                      ),
                      ul: ({ children }: { children: ReactNode }) => (
                        <ul
                          style={{
                            paddingLeft: '1.2rem',
                            marginBottom: '0.75rem',
                          }}
                        >
                          {children}
                        </ul>
                      ),
                      li: ({ children }: { children: ReactNode }) => (
                        <li style={{ marginBottom: '0.3rem' }}>{children}</li>
                      ),
                      strong: ({ children }: { children: ReactNode }) => (
                        <strong style={{ fontWeight: 600 }}>{children}</strong>
                      ),
                    }}
                  >
                    {aiBusy
                      ? `Generating explanation${dots}`
                      : explanationToShow}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExpComp;
